import { useState } from "react";
import { motion } from "framer-motion";
import { useToast } from "../../hooks/use-toast";
// import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { insertEmployeeSchema, insertEmployerSchema } from "../../shared/schema";
import { useMutation } from "@tanstack/react-query";
import { apiRequest } from "../../lib/queryClient";
import Container from "../../components/ui/container";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Card, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "../../components/ui/form";
import { Upload } from "lucide-react";

export default function Forms() {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("talent");
  const [file, setFile] = useState<File | null>(null);

  const employeeForm = useForm({
    // resolver: zodResolver(insertEmployeeSchema),
    defaultValues: {
      name: "",
      jobTitle: "",
      resumeUrl: ""
    }
  });

  const employerForm = useForm({
    // resolver: zodResolver(insertEmployerSchema),
    defaultValues: {
      name: "",
      details: "",
      message: ""
    }
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
      // In a real application, you would upload this file to your storage service
      // and then set the URL to the form's resumeUrl field
      employeeForm.setValue("resumeUrl", URL.createObjectURL(e.target.files[0]));
    }
  };

  const employeeMutation = useMutation({
    mutationFn: async (values: any) => {
      return apiRequest("POST", "/api/applications/employee", values);
    },
    onSuccess: () => {
      toast({
        title: "Application submitted",
        description: "We'll review your application and get back to you soon."
      });
      employeeForm.reset();
      setFile(null);
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive"
      });
    }
  });

  const employerMutation = useMutation({
    mutationFn: async (values: any) => {
      return apiRequest("POST", "/api/applications/employer", values);
    },
    onSuccess: () => {
      toast({
        title: "Inquiry submitted",
        description: "We'll get back to you shortly to discuss your needs."
      });
      employerForm.reset();
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive"
      });
    }
  });

  return (
    <section id="forms" className="py-20 bg-gray-50">
      <Container>
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-12">
          <h2 className="text-3xl font-bold">Join us to get started</h2>
        </div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid grid-cols-2 w-full">
                <TabsTrigger value="talent">Apply as Talent</TabsTrigger>
                <TabsTrigger value="employer">Hire Talent</TabsTrigger>
              </TabsList>
              <Card className="mt-6">
                <CardContent className="pt-6">
                  <TabsContent value="talent">
                    <Form {...employeeForm}>
                      <form >
                        <FormField
                          control={employeeForm.control}
                          name="name"
                          render={({ field }:any) => (
                            <FormItem>
                              <FormLabel>Full Name</FormLabel>
                              <FormControl>
                                <Input placeholder="John Doe" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={employeeForm.control}
                          name="jobTitle"
                          render={({ field }:any) => (
                            <FormItem>
                              <FormLabel>Job Title</FormLabel>
                              <FormControl>
                                <Input placeholder="Senior Developer" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={employeeForm.control}
                          name="resumeUrl"
                          render={({ field }:any) => (
                            <FormItem>
                              <FormLabel>Resume</FormLabel>
                              <FormControl>
                                <div className="flex gap-4 items-center">
                                  <Input
                                    type="file"
                                    accept=".pdf,.doc,.docx"
                                    onChange={handleFileChange}
                                    className="hidden"
                                    id="resumeUpload"
                                  />
                                  <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => document.getElementById('resumeUpload')?.click()}
                                    className="w-full"
                                  >
                                    <Upload className="mr-2 h-4 w-4" />
                                    {file ? file.name : "Upload Resume"}
                                  </Button>
                                </div>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <Button type="submit" className="w-full mt-6" disabled={employeeMutation.isPending}>
                          {employeeMutation.isPending ? "Submitting..." : "Submit Application"}
                        </Button>
                      </form>
                    </Form>
                  </TabsContent>
                  <TabsContent value="employer">
                    <Form {...employerForm}>
                      <form onSubmit={employerForm.handleSubmit((data:any) => employerMutation.mutate(data))} className="space-y-6">
                        <FormField
                          control={employerForm.control}
                          name="name"
                          render={({ field }:any) => (
                            <FormItem>
                              <FormLabel>Full Name</FormLabel>
                              <FormControl>
                                <Input placeholder="John Doe" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={employerForm.control}
                          name="details"
                          render={({ field }:any) => (
                            <FormItem>
                              <FormLabel>Company Details</FormLabel>
                              <FormControl>
                                <Textarea placeholder="Tell us about your company..." {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={employerForm.control}
                          name="message"
                          render={({ field }:any) => (
                            <FormItem>
                              <FormLabel>Project Requirements</FormLabel>
                              <FormControl>
                                <Textarea placeholder="Describe your project needs..." {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <Button type="submit" className="w-full" disabled={employerMutation.isPending}>
                          {employerMutation.isPending ? "Submitting..." : "Submit Inquiry"}
                        </Button>
                      </form>
                    </Form>
                  </TabsContent>
                </CardContent>
              </Card>
            </Tabs>
          </motion.div>
        </div>
      </Container>
    </section>
  );
}